import { Fragment, useState, useEffect, useCallback } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { Send, X, Loader2 } from 'lucide-react'
import { toast } from '../lib/toast.js'
import getActionDeliveries from '../functions/API/getActionDeliveries.js'
import postRetryActionDelivery from '../functions/API/postRetryActionDelivery.js'
import { DeliveryRow } from '../components/ActionActivity.js'

/**
 * Shows the outcome of a "Send test". The test delivery is sent asynchronously
 * (within a minute in production), so this polls the action's deliveries until
 * the test result lands. Test deliveries use dummy data and are never tied to a
 * real task instance.
 */
export default function ActionTestResultModal({ open, setOpen, actionId, actionName }) {
  const [deliveries, setDeliveries] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    if (!actionId) return;
    try {
      const res = await getActionDeliveries({ task_flow_action_id: actionId, limit: 5 });
      if (res.response.ok) setDeliveries((res.data.deliveries || []).filter((d) => d.event === 'test'));
    } catch (e) {
      // Non-fatal.
    } finally {
      setLoaded(true);
    }
  }, [actionId]);

  useEffect(() => {
    if (!open || !actionId) return;
    setDeliveries([]);
    setLoaded(false);
    load();
    const interval = setInterval(load, 4000);
    return () => clearInterval(interval);
  }, [open, actionId, load]);

  const handleRetry = async (id) => {
    try {
      const res = await postRetryActionDelivery(id);
      if (res.response.ok) { toast.success('Test re-queued.'); load(); }
      else toast.error('Could not retry the test.');
    } catch (e) {
      toast.error('Could not retry the test.');
    }
  }

  const latest = deliveries[0];
  const waiting = !latest || latest.status === 'pending' || latest.status === 'processing';

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={() => setOpen(false)}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-100" enterFrom="opacity-0" enterTo="opacity-90"
          leave="ease-in duration-100" leaveFrom="opacity-90" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-start justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-100" enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-100" leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-zinc-800 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-200 dark:border-zinc-700">
                  <div className="flex items-center gap-2 min-w-0">
                    <Send className="h-5 w-5 text-sky-600 dark:text-sky-400 shrink-0" />
                    <Dialog.Title as="h3" className="text-base font-semibold text-gray-900 dark:text-white truncate">
                      Test result{actionName ? ` — ${actionName}` : ''}
                    </Dialog.Title>
                  </div>
                  <button className="p-1 rounded hover:bg-gray-100 dark:hover:bg-zinc-700" onClick={() => setOpen(false)}>
                    <X className="h-4 w-4 text-gray-400" />
                  </button>
                </div>

                <div className="px-5 py-4 space-y-2 max-h-[60vh] overflow-y-auto">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    A test request with dummy data was sent (it isn't linked to any real task).
                  </p>

                  {waiting && (
                    <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 py-3">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Waiting for the test to be delivered…
                    </div>
                  )}

                  {deliveries.map((d) => <DeliveryRow key={d.id} delivery={d} onRetry={handleRetry} />)}

                  {loaded && deliveries.length === 0 && !waiting && (
                    <p className="text-sm text-gray-400 py-2">No test deliveries found yet.</p>
                  )}
                </div>

                <div className="flex flex-row-reverse px-5 py-3.5 border-t border-gray-200 dark:border-zinc-700">
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md bg-gray-900 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-800"
                    onClick={() => setOpen(false)}
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
